import { ServiceResponse } from '@nx-smartmonkey/shared/hooks';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { vehicleService } from '../../../../application/VehicleService';
import { Vehicle, VehicleUpdateProps } from '../../../../domain/vehicle/Vehicle';
import { useErrorMiddlewareOld } from '../../../../hooks/useErrorMiddlewareOld';
import { useShowSnackbarMessage } from '../../../../hooks/useShowSnackbarMessage';
import { getRetrieveVehicleKey } from '../../hooks';
import { UseRetrieveVehicleData } from '../../hooks/useRetrieveVehicle';

interface UseUpdateVehicleProps {
  vehicle: Vehicle;
  changes: VehicleUpdateProps;
}

type UseUpdateVehicleResponseProps = ServiceResponse<(updateProps: UseUpdateVehicleProps) => void, void>;

// Hooks act as 'Adapter' layer.
export const useUpdateVehicle = (): UseUpdateVehicleResponseProps => {
  const { sessionExpiredLogout } = useErrorMiddlewareOld();
  const queryClient = useQueryClient();

  const { showSnackbarMessage } = useShowSnackbarMessage();

  const { isLoading, isSuccess, isError, mutate } = useMutation<Vehicle, unknown, UseUpdateVehicleProps>(
    ({ vehicle, changes }: UseUpdateVehicleProps): Promise<Vehicle> => {
      vehicle.update(changes);
      return vehicleService.updateVehicle({
        vehicle,
      });
    },
    {
      onMutate: async ({ vehicle, changes }) => {
        const vehicleDetails = (await queryClient.getQueryData(getRetrieveVehicleKey())) as UseRetrieveVehicleData;

        // It should never enter here. Added for typing purposes
        if (!vehicleDetails) return;

        vehicle.update(changes);

        queryClient.setQueryData(getRetrieveVehicleKey(), { ...vehicleDetails, vehicle });
      },
      onError: (err: any, _variables: any) => {
        console.info(`Update Vehicle Error: `, err?.response);

        if (err?.response?.status === 401) {
          sessionExpiredLogout();
          return;
        }

        queryClient.invalidateQueries(getRetrieveVehicleKey());

        showSnackbarMessage({
          messageIntlId: `infoerror.${err?.response?.data?.messageId ?? `network_error`}`,
        });
      },
    }
  );

  return {
    error: isError,
    success: isSuccess,
    isLoading: isLoading,
    run: (updateProps: UseUpdateVehicleProps) => mutate(updateProps),
  };
};
